/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useServiceContext } from '../root'
import type { Document as ApiDocument } from '../services/api/types'
import { FsiType, SystemFolderType, type SystemFolderTypeType } from '../constants/fsi'

export interface Document {
  id: number
  name: string
  lastModified: string
  valid?: boolean
  type?: string
  [key: string]: any // Allow additional properties for flexibility
}

export interface DocumentFilter {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
}

export interface DocumentResponse {
  elements: Document[]
  totalElements: number
  hasNextPage?: boolean
}

const useDocumentsListService = () => {
  const context = useServiceContext()
  const { api, service } = context
  const { translate } = service.filter
  const { auth, access } = service

  // Helper function to safely parse timestamps
  const safeParseDate = (timestamp: number | string | undefined): string => {
    if (!timestamp) {
      return new Date().toISOString()
    }

    try {
      // Handle both Unix timestamp (seconds) and JavaScript timestamp (milliseconds)
      const date =
        typeof timestamp === 'string'
          ? new Date(timestamp)
          : new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp)

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp received:', timestamp)
        return new Date().toISOString()
      }

      return date.toISOString()
    } catch (error) {
      console.warn('Error parsing timestamp:', timestamp, error)
      return new Date().toISOString()
    }
  }

  const loadRecent = async (filter: DocumentFilter): Promise<DocumentResponse> => {
    try {
      // Use the enhanced API call with filtering parameters
      const response = await api.files.getRecentDocumentsFiltered({
        pageSize: filter.pageSize,
        pageStart: filter.pageStart,
        sortBy: filter.sortBy,
        sortDesc: filter.sortDesc
      })

      // Transform API response to match our interface with safe date parsing
      const transformedDocuments = response.elements.map((doc: ApiDocument) => {
        return {
          ...doc,
          id: doc.id,
          name: doc.name,
          lastModified: safeParseDate(doc.updatedAt || doc.createdAt)
        }
      })

      return {
        elements: transformedDocuments,
        totalElements: response.totalElements
      }
    } catch (error) {
      console.error('Failed to load recent documents:', error)
      // Fallback to empty result to satisfy return type
      return {
        elements: [],
        totalElements: 0
      }
    }
  }

  const loadFavorites = async (filter: DocumentFilter): Promise<DocumentResponse> => {
    try {
      // Build query parameters with required defaults for FavoritesParams
      const queryParams = {
        pageSize: filter.pageSize || 5,
        pageStart: filter.pageStart || 0,
        sortBy: filter.sortBy || 'updatedAt',
        sortDesc: filter.sortDesc !== undefined ? filter.sortDesc : true
      }

      // Use the real API call with filtering parameters
      const response = await api.files.getFavorites(queryParams)

      // Transform API response to match our interface with safe date parsing
      const transformedDocuments = response.elements.map((doc: any) => {
        return {
          ...doc,
          id: doc.id,
          name: doc.favoriteName ?? doc.name,
          lastModified: safeParseDate(doc.updatedAt || doc.createdAt)
        }
      })

      return {
        elements: transformedDocuments,
        totalElements: response.totalElements
      }
    } catch (error: any) {
      service.toast.error(translate(error?.body?.message || 'ERROR'))
      return {
        elements: [],
        totalElements: 0
      }
    }
  }

  const assembleReportFiltersFromFormValues = (values: any) => {
    const filter: any = {
      showObsoleted: values?.showObsoleted || false,
      systemFolderReportType:
        values?.viewType === SystemFolderType.EMAIL_UPLOADS ? SystemFolderType.EMAIL_UPLOADS : values?.type,
      systemDocumentsType: values?.type ? null : values?.viewType
    }

    if (values?.createdById) {
      filter.createdById = values.createdById
    }

    // Convert dates to timestamps if they exist
    if (values?.createdAtFrom) {
      const fromDate = values.createdAtFrom instanceof Date ? values.createdAtFrom : new Date(values.createdAtFrom)
      filter.createdAtFrom = fromDate.getTime()
    }

    if (values?.createdAtTo) {
      const toDate = values.createdAtTo instanceof Date ? values.createdAtTo : new Date(values.createdAtTo)
      // Set to end of day (23:59:59.999)
      toDate.setHours(23, 59, 59, 999)
      filter.createdAtTo = toDate.getTime()
    }

    return filter
  }

  const loadReports = async (queryParams: any, filter: any): Promise<DocumentResponse> => {
    try {
      // Assemble filter parameters from form values
      const assembledFilter = assembleReportFiltersFromFormValues(filter || {})

      // Merge queryParams (sorting, pagination) with filter params
      const apiParams = {
        ...queryParams,
        ...assembledFilter
      }

      // Call API to get reports
      const response = await api.reports.getReportsList(
        apiParams.systemDocumentsType,
        apiParams.systemFolderReportType,
        apiParams
      )

      // Filter out folders, only keep files
      const rawItems = response.children.elements.filter((item: any) => item.type !== FsiType.FOLDER)

      // Transform items to add necessary fields matching Document interface
      // Fetch user details for createdBy like legacy does
      const items: Document[] = rawItems.map((file: any) => ({
        ...file,
        lastModified: safeParseDate(file.updatedAt || file.createdAt),
        updatedOn: file.updatedAt,
        // Fetch user details using getUserById to match legacy behavior
        createdBy: file.createdById ? api.users.getUserById(file.createdById) : null,
        thumbnail: file.thumbnailId ? `/api/files/thumbnails/${file.thumbnailId}` : undefined
      }))

      // Get total count from API to enable pagination
      const totalCount = await api.reports.getReportsCount(
        apiParams.systemDocumentsType,
        apiParams.systemFolderReportType,
        apiParams
      )

      // Calculate if there's a next page based on current page and total count
      const currentPageEnd = (apiParams.pageStart + 1) * apiParams.pageSize
      const hasNextPage = currentPageEnd < totalCount

      return {
        elements: items,
        totalElements: totalCount,
        hasNextPage: hasNextPage
      }
    } catch (error) {
      console.error('Failed to load reports:', error)
      return {
        elements: [],
        totalElements: 0,
        hasNextPage: false
      }
    }
  }

  const loadPeriodicReview = async (filter: DocumentFilter): Promise<DocumentResponse> => {
    try {
      // Build query parameters, excluding undefined values
      const queryParams: Record<string, string | number | boolean | string[] | number[]> = {}
      if (filter.pageSize !== undefined) queryParams.pageSize = filter.pageSize
      if (filter.pageStart !== undefined) queryParams.pageStart = filter.pageStart
      if (filter.sortBy !== undefined) queryParams.sortBy = filter.sortBy
      if (filter.sortDesc !== undefined) queryParams.sortDesc = filter.sortDesc

      // Use the real API call with filtering parameters
      const response = await api.files.getPeriodicFiles(queryParams)

      // Transform API response to match our interface with safe date parsing
      const transformedDocuments = response.elements.map((doc: any) => {
        return {
          ...doc,
          id: doc.id,
          name: doc.name,
          lastModified: safeParseDate(doc.updatedAt || doc.createdAt)
        }
      })

      return {
        elements: transformedDocuments,
        totalElements: response.totalElements // FilesResponse doesn't have totalElements, use files.length
      }
    } catch (error: any) {
      service.toast.error(translate(error?.body?.message || 'ERROR'))
      return {
        elements: [],
        totalElements: 0
      }
    }
  }

  const getFsiNameTooltip = (fsi: { name: string; type?: string; valid?: boolean; isReport?: boolean }): string => {
    let tooltip = fsi.name

    if (fsi.valid === false) {
      tooltip += ' (Invalid)'
    }

    if (fsi.isReport) {
      tooltip += ' (Report)'
    }

    return tooltip
  }

  const loadRegular = async (folderId: number, queryParams: any, filter: any): Promise<DocumentResponse> => {
    try {
      // Assemble filter parameters
      const filterParams = assembleRegularFolderFiltersFromFormValues(filter)

      // Merge queryParams (sorting, pagination) with filter params
      const apiParams = {
        ...filterParams,
        ...queryParams
      }

      // Call API to get folder contents with filter and query params
      const response = await api.files.getFiles(folderId, apiParams)

      // Get elements from response
      const elements = response.elements || []

      // Separate folders and documents
      const folders = elements.filter((item: any) => item.type === 'FOLDER')
      const documents = elements.filter((item: any) => item.type !== 'FOLDER')

      // Apply sorting to documents (folders always come first)
      if (queryParams?.sortBy) {
        documents.sort((a: any, b: any) => {
          let aVal, bVal

          switch (queryParams.sortBy) {
            case 'NAME':
              aVal = a.name?.toLowerCase() || ''
              bVal = b.name?.toLowerCase() || ''
              break
            case 'VERSION':
              aVal = a.fileVersionNo || 0
              bVal = b.fileVersionNo || 0
              break
            case 'UPDATED_ON':
              aVal = a.updatedOn || 0
              bVal = b.updatedOn || 0
              break
            case 'WORKFLOW':
              aVal = a.workflow?.[0]?.formattedId?.toLowerCase() || ''
              bVal = b.workflow?.[0]?.formattedId?.toLowerCase() || ''
              break
            default:
              aVal = a.name?.toLowerCase() || ''
              bVal = b.name?.toLowerCase() || ''
          }

          if (aVal < bVal) return queryParams.sortDesc ? 1 : -1
          if (aVal > bVal) return queryParams.sortDesc ? -1 : 1
          return 0
        })
      }

      // Return combined results with folders first
      return {
        elements: [...folders, ...documents],
        totalElements: response.totalElements ?? elements.length
      }
    } catch (error) {
      console.error('Failed to load regular folder contents:', error)
      return {
        elements: [],
        totalElements: 0
      }
    }
  }

  const assembleRegularFolderFiltersFromFormValues = (values: any) => {
    const filter: any = {}

    if (values.name) {
      filter.name = values.name
    }

    if (values.state) {
      filter.state = values.state
    }

    if (values.blindingStatus) {
      filter.blindingStatus = values.blindingStatus
    }

    if (values.artifacts && values.artifacts.length > 0) {
      filter.artifacts = values.artifacts
    }

    // Handle date range for lastUpdated
    if (values.lastUpdated === 'range') {
      if (values.lastUpdatedFrom) {
        filter.updatedOnFrom = new Date(values.lastUpdatedFrom).getTime()
      }
      if (values.lastUpdatedTo) {
        const toDate = new Date(values.lastUpdatedTo)
        toDate.setHours(23, 59, 59, 999)
        filter.updatedOnTo = toDate.getTime()
      }
    } else if (values.lastUpdated && values.lastUpdated !== '') {
      const currentTime = new Date()
      const from = new Date()

      switch (values.lastUpdated) {
        case 'p1h':
          from.setHours(from.getHours() - 1)
          break
        case 'p24h':
          from.setHours(from.getHours() - 24)
          break
        case 'p1w':
          from.setDate(from.getDate() - 7)
          break
        case 'p1m':
          from.setMonth(from.getMonth() - 1)
          break
        case 'p1y':
          from.setMonth(from.getMonth() - 12)
          break
      }

      filter.updatedOnFrom = from.getTime()
      filter.updatedOnTo = currentTime.getTime()
    }

    return filter
  }

  const getReportFolderDefinition = (type: SystemFolderTypeType) => {
    let name = ''
    switch (type) {
      case SystemFolderType.DOCUMENT_MIGRATIONS_AND_EXPORTS:
        name = translate('DOCUMENTS_MIGRATION_EXPORTS')
        break
      case SystemFolderType.REPORTS:
        name = translate('REPORTS_EXPORT_TITLE')
        break
      case SystemFolderType.SETTINGS_AND_SYSTEM_EXPORTS_IMPORTS:
        name = translate('SETTINGS_AND_SYSTEM_ITEMS_EXPORTS_IMPORTS')
        break
      case SystemFolderType.EMAIL_UPLOADS:
        name = translate('SYSTEM_DOCUMENTS_EMAIL_UPLOADS')
        break
    }

    // Decode HTML entities (e.g., &amp; to &) for proper text display
    const decodeHtmlEntities = (text: string) => {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = text
      return textarea.value
    }

    return {
      id: 0,
      key: type,
      name: name,
      folderPathText: decodeHtmlEntities(name),
      systemFolderType: type,
      type: FsiType.FOLDER,
      isAbstractFolder: true,
      path: [],
      valid: true // to enable actions menu
    }
  }

  const getSystemDocumentsDefinition = (queryParams: any, userRole: string): Promise<DocumentResponse> => {
    let foldersList: any[] = []

    // Check if user is ETMF_SPONSOR
    if (userRole === 'ETMF_SPONSOR') {
      foldersList = [getReportFolderDefinition(SystemFolderType.REPORTS)]
    } else {
      foldersList = [
        getReportFolderDefinition(SystemFolderType.DOCUMENT_MIGRATIONS_AND_EXPORTS),
        getReportFolderDefinition(SystemFolderType.REPORTS),
        getReportFolderDefinition(SystemFolderType.SETTINGS_AND_SYSTEM_EXPORTS_IMPORTS),
        getReportFolderDefinition(SystemFolderType.EMAIL_UPLOADS)
      ]
    }

    // Add INIT_ETMF_FILES folder if available
    const initFilesFolder = auth.getBrowser(SystemFolderType.INIT_ETMF_FILES)
    if (initFilesFolder && initFilesFolder.id) {
      foldersList.push({
        id: initFilesFolder.id,
        key: initFilesFolder.id,
        name: initFilesFolder.name,
        systemFolderType: SystemFolderType.INIT_ETMF_FILES,
        type: FsiType.FOLDER,
        valid: true, // to enable actions menu
        isFavorite: initFilesFolder.isFavorite
      })
    }

    // Add FORMS folder if user has access
    if (access.isAllowed('documentsAccessForms')) {
      foldersList.push({
        id: 4,
        key: 4,
        name: translate('FORMS'),
        systemFolderType: SystemFolderType.FORMS,
        type: FsiType.FOLDER,
        valid: true, // to enable actions menu
        isFavorite: false
      })
    }

    // Add TEMPLATES folder if user has access
    if (access.isAllowed('documentsAccessTemplates')) {
      foldersList.push({
        id: 3,
        key: 3,
        name: translate('TEMPLATES'),
        systemFolderType: SystemFolderType.TEMPLATES,
        type: FsiType.FOLDER,
        valid: true, // to enable actions menu
        isFavorite: false
      })
    }

    // Sort folders by name
    foldersList = foldersList.sort((folder1, folder2) => {
      const compareResult = folder1.name.localeCompare(folder2.name, undefined, { sensitivity: 'base' })
      return queryParams.sortDesc ? -compareResult : compareResult
    })

    const promise = Promise.resolve({
      elements: [...foldersList],
      totalElements: foldersList.length
    })

    return promise
  }

  return {
    loadRecent,
    loadFavorites,
    loadReports,
    loadPeriodicReview,
    loadRegular,
    getFsiNameTooltip,
    assembleRegularFolderFiltersFromFormValues,
    assembleReportFiltersFromFormValues,
    getReportFolderDefinition,
    getSystemDocumentsDefinition
  }
}

export default useDocumentsListService
